import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from './api.ts';
import type { PublicUser, Role } from '../../../shared/types.ts';

const RANK: Record<Role, number> = { leitor: 1, gerente: 2, superadmin: 3 };

interface AuthState {
  user: PublicUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (minimum: Role) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ user: PublicUser }>('/auth/me')
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.post<{ user: PublicUser }>('/auth/login', { email, password });
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch((err) => {
      // Sessão já inválida do lado do servidor: limpar local é o comportamento certo.
      if (!(err instanceof ApiError) || err.status !== 401) throw err;
    });
    setUser(null);
  }, []);

  const can = useCallback(
    (minimum: Role) => (user ? RANK[user.role] >= RANK[minimum] : false),
    [user],
  );

  const value = useMemo(
    () => ({ user, loading, login, logout, can }),
    [user, loading, login, logout, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return context;
}

export const ROLE_LABEL: Record<Role, string> = {
  superadmin: 'Superadmin',
  gerente: 'Gerente',
  leitor: 'Leitor',
};
